import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Loader2, TrendingDown, Users, Download, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const MarketingLeadsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/leads")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/marketing/leads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "marketing_leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  if (loading) {
    return (
      <div className="glass p-6 flex items-center gap-2 text-cream-dim">
        <Loader2 size={16} className="animate-spin" /> Loading leads…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="glass p-6" data-testid="marketing-leads-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Marketing Leads</div>
          <h3 className="font-display text-xl text-cream mt-1">Last 30 Days</h3>
        </div>
        <Button onClick={downloadCsv} className="btn-ghost-gold h-9 px-4 text-xs"
                data-testid="marketing-leads-csv">
          <Download size={12} className="mr-2" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-5">
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20" data-testid="marketing-leads-count">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-cream-dim">
            <Users size={11} /> Captured
          </div>
          <div className="metric text-2xl text-gold mt-1">{data.count}</div>
        </div>
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20" data-testid="marketing-leads-domains">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Unique Domains</div>
          <div className="metric text-2xl text-gold mt-1">{data.unique_domains}</div>
        </div>
        <div className="p-3 rounded bg-navy-deep border border-gold-dim/20" data-testid="marketing-leads-avg">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">Avg Score</div>
          <div className="metric text-2xl text-gold mt-1">{data.avg_traction_score}</div>
        </div>
      </div>

      {/* Top opportunity domains */}
      {data.top_opportunity_domains?.length > 0 ? (
        <div className="mt-5 pt-5 border-t border-gold-dim/20">
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3 flex items-center gap-1.5">
            <TrendingDown size={11} /> Top Opportunity Domains (lowest traction scores)
          </div>
          <div className="space-y-2">
            {data.top_opportunity_domains.map((d, i) => (
              <div key={i}
                   className="flex items-center justify-between p-2 rounded bg-navy-deep/60 border border-gold-dim/10 text-sm"
                   data-testid={`marketing-leads-domain-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-gold truncate">{d.domain}</span>
                  <span className="text-[10px] text-cream-dim">
                    {d.lead_count} lead{d.lead_count === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-cream-dim">{d.latest_grade || "—"}</span>
                  <span className={`font-mono text-sm font-bold ${
                    d.min_score < 40 ? "text-[color:var(--auditiq-critical)]"
                      : d.min_score < 55 ? "text-orange-400"
                      : d.min_score < 70 ? "text-gold"
                      : "text-[color:var(--auditiq-success,#5bbf85)]"
                  }`}>
                    {d.min_score}
                  </span>
                  {d.latest_email && (
                    <a href={`mailto:${d.latest_email}`}
                       title={d.latest_email}
                       className="text-electric hover:text-gold"
                       data-testid={`marketing-leads-email-${i}`}>
                      <Mail size={12} />
                    </a>
                  )}
                  <a href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer"
                     className="text-cream-dim hover:text-gold">
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-5 pt-5 border-t border-gold-dim/20 text-sm text-cream-dim">
          No leads captured yet. Share <span className="text-gold font-mono">/free-audit</span> on LinkedIn,
          in your email signature, or embed it in your Wix site to start generating inbound leads.
        </div>
      )}
    </div>
  );
};

export default MarketingLeadsCard;
